import {
  BausteinAuswahlStatus,
  OutbuildingSizeCategory,
  Produktlinienname,
  SpartenType,
  TaasTarifrisikoschluesselVWGV2,
  TaasVwgV2Data,
  VorschadenArt
} from '@ps20/bff-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  parseDatum
} from '@ps20-frontend/preis-lib';
import {
  App,
  convertProduktNameToTaasProduktName,
  convertVwgV2TaasBausteinToTarifrisikoschluessel,
  Produkt
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { IAppState } from '../../appState';
import { risikoortActionHistory } from '../../risikoortActionHistory';
import { selectExtremwetterschutzActiveValue } from '../../../app/shared/selectors/elementar-selbstbehalt.selector';
import {
  isNebengebaeudeLeistungSelectable,
} from '../../../app/shared/selectors/gebaeude.selector';
import { getGefahrenVorversicherung } from '../inDashboardUebernehmenSaga';
import { selectHausTechnikSchutzDeselected } from '../../../app/shared/selectors/produkt.selector';
import { selectIsNeugeschaeft } from '../../../app/shared/selectors/vertragsoption.selector';
import { setGisDataToVwgData } from './gisDaten.transformer';

// Beibehaltung and Ersatzgeschaeft both hide/bypass the Vorversicherung question (it's answered
// implicitly - a Vorvertrag exists, so there is a previous policy by definition), so an
// unanswered vorversicherungVorhanden must still resolve to true whenever a Vorvertrag exists.
export function resolveVorversicherung(state: IAppState): boolean {
  return getGefahrenVorversicherung(state)?.length > 0
    ? true
    : state.vertragsoptionen.vorversicherungVorhanden ?? !selectIsNeugeschaeft(state);
}

export function resolveOutdoorPool(state: IAppState): boolean {
  const selectedName = state.produkt?.produktlinien?.find(pl => pl?.selected)?.produktName;
  if (selectedName !== Produktlinienname.KOMFORT) {
    return state.buildingFeatures.poolOutdoors;
  }
  if (!state.buildingFeatures?.poolOutdoors) {
    return false;
  }
  if (selectHausTechnikSchutzDeselected(state)) {
    return false;
  }
  return state.buildingFeatures.aussenpoolInHts !== false;
}

type SalesProductBuilder = (
  produkt: Produkt,
  convert: (baustein: string) => TaasTarifrisikoschluesselVWGV2
) => { [index: string]: BausteinAuswahlStatus<TaasTarifrisikoschluesselVWGV2>[] };

export const buildTaasVWGDataForStrecke = (state: IAppState): TaasVwgV2Data =>
  buildTaasVWGData(state, buildBasicSalesProductsForStrecke);
export const buildTaasVWGDataForShop = (state: IAppState): TaasVwgV2Data =>
  buildTaasVWGData(state, buildBasicSalesProductsForShop);

function buildTaasVWGData(state: IAppState, buildSalesProduct: SalesProductBuilder): TaasVwgV2Data {
  let vwgData: TaasVwgV2Data = generateBasicTaasVWGData(state);

  setNebengebaeudeData(vwgData, state);
  vwgData.vorversicherung = resolveVorversicherung(state);
  vwgData = setGisDataToVwgData(vwgData, state.gisDaten);

  vwgData.salesProducts = buildSalesProduct(
    state.produkt,
    convertVwgV2TaasBausteinToTarifrisikoschluessel
  );
  addHausTechnikSchutzBaustein(vwgData, state);

  if (state.vertragsoptionen?.beibehalten) {
    if (state.gebaeude.plzFaktorSnd) {
      vwgData.plzFaktorSnd = state.gebaeude.plzFaktorSnd;
    }
    if (state.gebaeude.plzFaktorErdb) {
      vwgData.plzFaktorErdb = state.gebaeude.plzFaktorErdb;
    }
    vwgData.plzFaktorGla = state.gebaeude.plzFaktorGla;
    vwgData.plzFaktorHws = state.gebaeude.plzFaktorHws;
    vwgData.ueberSchwemmungSelbstbehalt = state.ueberschwemmungSelbstbeteiligung
      ? state.ueberschwemmungSelbstbeteiligung
      : undefined;
    vwgData.erdbebenSelbstbehalt = state.erdbebenSelbstbetailigung
      ? state.erdbebenSelbstbetailigung
      : undefined;
  }

  if (!state.vertragsoptionen.beibehalten) {
    vwgData.extremwetterschutzSelbstbeteiligung = selectExtremwetterschutzActiveValue(state);
  }
  return vwgData;
}

function setNebengebaeudeData(vwgData: TaasVwgV2Data, state: IAppState): void {
  if (!isNebengebaeudeLeistungSelectable(state)) {
    return;
  }

  if (state.gebaeude.nebengebaeude.available !== true) {
    return;
  }

  const anyProduktlinieLeistungen = state.produkt?.produktlinien?.[0]?.leistungen;
  if (!anyProduktlinieLeistungen) {
    return;
  }

  const versicherteNebengebaeude = anyProduktlinieLeistungen.find(
    l => l.id === 'versicherteNebengebaeude'
  );

  if (versicherteNebengebaeude?.abgewaehlt) {
    vwgData.gebaeude.nebengebaeudeQM = 0;
    return;
  }

  if (state.gebaeude.nebengebaeude.sizeCategory) {
    vwgData.gebaeude.outbuildingSizeCategory = state.gebaeude.nebengebaeude.sizeCategory;

    if (
      state.gebaeude.nebengebaeude.sizeCategory === OutbuildingSizeCategory.CATEGORY_OVER_100 &&
      state.gebaeude.nebengebaeude.totalArea > 0
    ) {
      vwgData.gebaeude.nebengebaeudeQM = state.gebaeude.nebengebaeude.totalArea;
    }
  } else if (state.gebaeude.nebengebaeude.totalArea > 0) {
    vwgData.gebaeude.nebengebaeudeQM = state.gebaeude.nebengebaeude.totalArea;
  }
}

function generateBasicTaasVWGData(state: IAppState): TaasVwgV2Data {
  const { plz, ort, strasse, hausnummer, longtitude, latitude } = state.risikoort;
  if (
    !state.risikoort.isImmobilieImAusland &&
    ((!state.vertragsoptionen.beibehalten &&
        (!longtitude || !latitude) &&
        (!plz || !ort || !strasse || !hausnummer)) ||
      (longtitude && latitude && (!plz || !ort || !strasse)))
  ) {
    PS20Logger.info(
      App.VWG_V2,
      'Tarifierung mit leeren Adressattributen angestoßen.',
      JSON.stringify(risikoortActionHistory.getActionHistory())
    );
  }
  return {
    vorversicherung: true,
    spartenType: SpartenType.WOHNGEBAEUDE_V2,
    gebaeude: {
      ferienhaus: state.buildingFeatures.vacationHome,
      holzhaus: state.buildingFeatures.woodenHouse,
      dachbelagWeich: state.buildingFeatures.softRoofCovering,
      flachdach: state.buildingFeatures.flatRoof,
      adresse: {
        plz: state.risikoort.plz,
        ort: state.risikoort.ort,
        strasse: state.risikoort.strasse,
        hausnummer: state.risikoort.hausnummer,
        latitude: state.risikoort.latitude,
        longtitude: state.risikoort.longtitude,
        auslaendischeAdresse: state.risikoort.isImmobilieImAusland,
      },
      baujahr: state.gebaeude.baujahr,
      wohnflaecheQM: state.gebaeude.wohnflaeche,
      kellerVorhanden: state.buildingFeatures.basement,
      kellerMitWohnflaeche: state.buildingFeatures.basementWithLivingSpace ?? false,
      outbuildingPresent:
        state.gebaeude.nebengebaeude.available === true && isNebengebaeudeLeistungSelectable(state),
      leerstehend: state.buildingFeatures.notPermanentlyOccupied,
      schwimmbad: state.buildingFeatures.pool,
      solarthermie: state.buildingFeatures.solarThermal,
      photovoltaik: state.buildingFeatures.photovoltaic,
      geothermie: state.buildingFeatures.geothermal,
      denkmalschutz: state.buildingFeatures.heritageProtection,
      vorschaeden: [
        {
          vorschadenArt: VorschadenArt.ELEMENTAR,
          anzahl: state.vorschaeden.elementarVorschaden.includeInTarifierung
            ? state.vorschaeden.elementarVorschaden.count
            : 0,
          manuallyEntered: state.vorschaeden.elementarVorschaden.touched,
        },
        {
          vorschadenArt: VorschadenArt.FEUER,
          anzahl: state.vorschaeden.feuerVorschaden.includeInTarifierung
            ? state.vorschaeden.feuerVorschaden.count
            : 0,
          manuallyEntered: state.vorschaeden.feuerVorschaden.touched,
        },
        {
          vorschadenArt: VorschadenArt.LEITUNGSWASSER,
          anzahl: state.vorschaeden.leitungswasserVorschaden.includeInTarifierung
            ? state.vorschaeden.leitungswasserVorschaden.count
            : 0,
          manuallyEntered: state.vorschaeden.leitungswasserVorschaden.touched,
        },
        {
          vorschadenArt: VorschadenArt.STURM_HAGEL,
          anzahl: state.vorschaeden.sturmhagelVorschaden.includeInTarifierung
            ? state.vorschaeden.sturmhagelVorschaden.count
            : 0,
          manuallyEntered: state.vorschaeden.sturmhagelVorschaden.touched,
        },
      ],
    },
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    salesProducts: null,
    urbeginndatum: state.vertragsoptionen?.urbeginn,
    internerVertragsbezugs: state.taasInfo?.internerVertragsbezugs,
    buildingFeatures: {
      rented: state.buildingFeatures.rented,
      indoorPool: state.buildingFeatures.poolIndoors,
      outdoorPool: resolveOutdoorPool(state),
      heatPump: state.buildingFeatures.geothermal,
      gasHeating: state.buildingFeatures.gasHeating,
      oilHeating: state.buildingFeatures.oilHeating,
      pelletHeating: state.buildingFeatures.pelletHeating,
      districtHeating: state.buildingFeatures.districtHeating,
    },
  };
}

function addHausTechnikSchutzBaustein(vwgData: TaasVwgV2Data, state: IAppState): void {
  const HTS_LEISTUNG_ID = 'hausTechnikSchutz';
  const htsBaustein: BausteinAuswahlStatus<TaasTarifrisikoschluesselVWGV2> = {
    schluessel: TaasTarifrisikoschluesselVWGV2.GEBAEUDETECHNIK,
    ausgewaehlt: true,
    isGefahr: false,
    isInklusiv: true
  };

  for (const produktlinie of state.produkt.produktlinien) {
    const taasKey = convertProduktNameToTaasProduktName(produktlinie.produktName);
    const bausteine = vwgData.salesProducts?.[taasKey];
    if (!bausteine) {
      continue;
    }

    if (produktlinie.produktName === Produktlinienname.KOMFORT) {
      const shouldInclude = state.vertragsoptionen?.beibehalten
        ? state.taasInfo?.deltafaktoren?.deltafaktoren?.some(
            d => d.baustein === TaasTarifrisikoschluesselVWGV2.GEBAEUDETECHNIK
          )
        : !produktlinie.leistungen?.find(l => l.id === HTS_LEISTUNG_ID)?.abgewaehlt;
      if (shouldInclude) {
        bausteine.push(htsBaustein);
      }
    }
  }
}
